import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import type { ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import SectionActions from "../../../../components/ResumeBuilder/SectionActions";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { convertArrayToUl } from "../../../../handlers/utilityFunctions";
import { AdditionalInfo } from "../../../../models/resume/additionalInfo";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";

interface PatchLedger {
	additionInfo: { data: Parameters<typeof convertArrayToUl>[0] };
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const AdditionalInformation = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const patchSection = usePatchResumeSection();

	//Declaring the additional information state
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [additionalInfo, setAdditionalInfo] = useState(resumeData.extras.additionalInfo);

	const [, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	useEffect(() => {
		setAdditionalInfo(resumeData.extras.additionalInfo);
	}, [resumeData]);

	//submission handler
	const addAdditionalInfo = () => {
		const payload = new AdditionalInfo(additionalInfo);
		const request = payload.getAdditionalInfoRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.ADDITIONAL_INFO, data: request } as never, {
			onSuccess: successPatchAdditionalInfo,
			onError: errorPatchAdditionalInfo,
		});
	};

	function successPatchAdditionalInfo(ledger: PatchLedger) {
		const resArray = ledger.additionInfo.data;

		const additionalInfoList = convertArrayToUl(resArray);

		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					additionalInfo: additionalInfoList,
				},
			};
		});

		navigate("/resume/extras");
	}

	function errorPatchAdditionalInfo(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="w-full md:max-w-2xl">
				<div className="mb-3 flex items-start justify-between gap-3">
					<p className="text-gray-700">Add anything else you want employers to know.</p>
					<Tips content={tips.additionalInformation} />
				</div>
				<TextEditor value={additionalInfo} setValue={setAdditionalInfo} placeholder="Add your details here..." />
				<SectionActions className="flex items-center justify-end gap-2">
					<Button type="button" variant="secondary" onClick={() => navigate("/resume/extras")}>
						Cancel
					</Button>
					<Button type="button" onClick={addAdditionalInfo}>
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Continue"
						)}
					</Button>
				</SectionActions>
			</div>
		</div>
	);
};

export default AdditionalInformation;
